import type { AddonGroupKind } from "@/lib/api/types";

/**
 * Splitting a plate's selected addons into WHICH DISH and WHAT WAS ADDED.
 *
 * Every staff screen used to render the whole selection as one flat run — a
 * bill line read "بيتزا — كبير · كاتشب · جبنة زيادة", where the size, the sauce
 * and the paid extra are typographically identical. A cashier scanning for
 * "which size did table 4 order" had to know the menu to parse it, and a cook
 * read "+ كبير" as something to add rather than as the dish to make.
 *
 * A VARIANT group names which version of the dish was sold (D59), so its option
 * belongs WITH the dish name; an EXTRA is added to that dish and belongs under
 * it. Nothing here changes a price, a total or how plates group — only where a
 * name is drawn.
 */

/** The shape this needs off a plate's snapshot; every caller's type is wider. */
export type PlateAddonLike = {
  nameAr: string;
  nameEn?: string | null;
  /** Snapshotted at sale (D59). Absent on a plate sold before it existed. */
  groupKind?: AddonGroupKind;
  /** The pre-D59 snapshot (D58), kept as the fallback below. */
  groupMinSelect?: number;
  groupMaxSelect?: number | null;
};

/**
 * Mirrors the server's axis rule in `item-profitability.policy.ts` — the plate's
 * own `kind` first, then a pre-D59 plate's snapshotted bounds under the old
 * inference, which is precisely what the `kind` column's backfill applied.
 *
 * Deliberately no third tier reading today's menu: the report has one because a
 * missing breakdown is a hole in a figure, but a bill line is just a label, and
 * re-reading the live menu would make an old bill's LAYOUT shift when someone
 * edits the menu. An unclassified plate simply reads as it always did.
 */
export function isVariantAddon(addon: PlateAddonLike): boolean {
  if (addon.groupKind !== undefined) return addon.groupKind === "VARIANT";
  if (addon.groupMinSelect !== undefined) {
    return addon.groupMinSelect > 0 && addon.groupMaxSelect === 1;
  }
  return false;
}

/**
 * The plate's addons in two runs, each keeping its original order — which is
 * the menu's `sortOrder`, so "Small · Medium · Large" never comes out shuffled.
 */
export function splitPlateAddons<T extends PlateAddonLike>(
  addons: readonly T[],
): { variants: T[]; extras: T[] } {
  const variants: T[] = [];
  const extras: T[] = [];
  for (const addon of addons) {
    (isVariantAddon(addon) ? variants : extras).push(addon);
  }
  return { variants, extras };
}
